"use client";

import clsx from "clsx";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCTS_REQUIRING_HOURS,
  PRODUCT_ORDER,
  PRODUCT_STEP_DETAILS,
  ProductFocus,
  STEP_KEY_TO_VIEW,
} from "@/constants/onboarding";
import { useOnboardingModal } from "@/contexts/onboarding/OnboardingModalContext";
import { AgencyVerificationStatus } from "@/enums/agencies";
import { useConfigurationStatus } from "@/hooks/useConfigurationStatus";
import { useOrg } from "@/hooks/useOrg";
import { ConfigurationStatus } from "@/utils/onboarding/configDetection";
import { shouldOfferPhoneVerificationStep } from "@/utils/verification/missingTwilioFields";
import SetupCard, { SetupStep } from "./SetupCard";

interface Step extends SetupStep {
  key: string;
}

// STEP_KEY_TO_VIEW is imported from @/constants/onboarding (single source of truth)

// Config status field for each step key (used to determine completion)
const STEP_COMPLETION_MAP: Record<string, keyof ConfigurationStatus> = {
  directorySetup: "hasDirectory",
  inboundScript: "hasInboundScript",
  callConcurrency: "hasCallConcurrency",
  outboundScript: "hasOutboundScript",
  createAssistant: "hasAssistant",
};

// Get product-specific steps based on product type (derived from PRODUCT_STEP_DETAILS)
function getProductSteps(
  product: ProductFocus,
  configStatus: ConfigurationStatus | null
): Array<{ key: string; completed: boolean }> {
  return PRODUCT_STEP_DETAILS[product].map(({ stepKey }) => {
    const configField = STEP_COMPLETION_MAP[stepKey];
    const completed = configField ? (configStatus?.[configField] ?? false) : false;
    return { key: stepKey, completed };
  });
}

// Get flow steps for display
function getFlowStepsForDisplay(
  productFocus: ProductFocus[],
  configStatus: ConfigurationStatus | null,
  phoneVerificationCompleted: boolean,
  offerPhoneVerification: boolean
): Array<{ key: string; completed: boolean }> {
  const needsHours = productFocus.some((p) => PRODUCTS_REQUIRING_HOURS.includes(p));

  return [
    // Always show focus selection first
    { key: "selectFocus", completed: productFocus.length > 0 },
    // Company info
    { key: "companyInfo", completed: configStatus?.hasCompanyInfo ?? false },
    // Languages
    { key: "languageSelection", completed: true },
    // Business hours if needed
    ...(needsHours
      ? [{ key: "businessHours", completed: configStatus?.hasBusinessHours ?? false }]
      : []),
    // Notifications
    { key: "confirmNotifications", completed: configStatus?.hasNotifications ?? false },
    // Product-specific steps
    ...PRODUCT_ORDER.filter((product) => productFocus.includes(product)).flatMap((product) =>
      getProductSteps(product, configStatus)
    ),
    ...(productFocus.includes("outbound") && offerPhoneVerification
      ? [{ key: "phoneVerification", completed: phoneVerificationCompleted }]
      : []),
  ];
}

export default function FinishSetupSection() {
  const { org } = useOrg();
  const { openModal } = useOnboardingModal();
  const configStatus = useConfigurationStatus();

  const { t } = useTranslation("onboarding");
  const { t: tStats } = useTranslation("stats");

  // Get onboarding completion status
  const onboardingDetails = org?.details?.onboarding;
  const gailFocus = onboardingDetails?.gailFocus ?? [];

  // Get product focus
  const productFocus = useMemo<ProductFocus[]>(() => {
    return gailFocus.filter((f): f is ProductFocus =>
      ["inbound", "outbound", "gailgpt"].includes(f)
    );
  }, [gailFocus]);

  // Helper to open the onboarding modal at a specific step
  const handleStepClick = useCallback(
    (stepKey: string) => {
      const viewType = STEP_KEY_TO_VIEW[stepKey as keyof typeof STEP_KEY_TO_VIEW];
      if (viewType) {
        openModal(viewType);
      }
    },
    [openModal]
  );

  // Build the step list for display
  const steps = useMemo<Step[]>(() => {
    const verificationStatus = org?.details?.agencyVerificationStatus;
    const phoneVerificationCompleted =
      verificationStatus === AgencyVerificationStatus.Pending ||
      verificationStatus === AgencyVerificationStatus.Verified;
    const displaySteps = getFlowStepsForDisplay(
      productFocus,
      configStatus,
      phoneVerificationCompleted,
      shouldOfferPhoneVerificationStep(org)
    );

    return displaySteps.map((step) => ({
      key: step.key,
      isCompleted: step.completed,
      title: t(`milestoneSteps.${step.key}`),
      action: () => handleStepClick(step.key),
    }));
  }, [productFocus, configStatus, t, handleStepClick, org]);

  const completionPercentage = useMemo(() => {
    if (steps.length === 0) return 0;
    const completedCount = steps.filter((step) => step.isCompleted).length;
    return Math.round((completedCount / steps.length) * 100);
  }, [steps]);

  return (
    <div className={clsx("flex", "flex-col", "gap-4", "h-full")}>
      <h2 className={clsx("text-900")}>{tStats("emptyState.finishSetup.title")}</h2>
      <SetupCard
        isLoading={false}
        steps={steps}
        completionPercentage={completionPercentage}
        completionLabel={tStats("emptyState.complete", {
          value: completionPercentage,
        })}
      />
    </div>
  );
}
